import API from 'core/api'
import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAsync } from 'react-async'
import styled from 'styled-components'
import Card from 'components/Card/Card'
import { getAccessToken, getConfig, getPartnerId } from 'core/singleton'
import { getLocation, requestBridge, throttle } from 'core/util/default'
import { useHistory, useLocation } from 'react-router-dom'
import { useRecoilState, useRecoilValue } from 'recoil'
import PageContent from 'components/Page/PageContent'
import Loading from 'components/Loading/Loading'
import EmptyItem from 'components/EmptyItem/EmptyItem'
import PageHeader from 'components/Page/PageHeader'
import ButtonBack from 'components/Button/ButtonBack'
import ButtonSearch from 'components/Button/ButtonSearch'
import SearchInput from 'components/Input/SearchInput'
import ButtonDelete from 'components/Button/ButtonDelete'
import { useTranslation } from 'react-i18next'
import { BRIDGE, ONLY_POST_MSG_PARTNERS } from 'core/constants/bridge'
import { IS_OPEN_LINK, STORE_ROOT_PROPS } from 'core/store/global'
import { KEYWORD_UPDATE } from 'core/store/search'
import { getExecutorForPartner } from 'core/util/partner'
import { getUrlQueryParameter, moveToLink, parseObjectToQueryString } from 'core/util/url'
import { filterObject } from 'core/util/object'

import { GAHelper } from 'core/ga'
import { GA_EVENT } from 'core/ga/enums'
import { useParams } from 'react-router'
import { decodeString } from '../../core/util/search'
import { LINK_MOVE_TYPE } from 'core/constants'
import { STORE_AIRBRIDGE, STORE_MARKETING_GTM } from 'core/store/analysis'
import { BroadcastParams } from '@types'

interface PageSearchResultProps {
  keyword: string
}

const PageSearchResult: React.FC<PageSearchResultProps> = () => {
  const { t } = useTranslation()
  const query = getUrlQueryParameter()
  const { keyword } = useParams<{ keyword: string }>()
  // const keyword = decodeString(props.keyword)
  const isOpenLink = useRecoilValue(IS_OPEN_LINK)
  const airbridge = useRecoilValue(STORE_AIRBRIDGE)
  const marketingGTM = useRecoilValue(STORE_MARKETING_GTM)
  const [nextKey, setNextKey] = useState<string>('init')
  // const [activeTab, setActiveTab] = useState('vod')
  // const latestActiveTab = useRef<string>(activeTab)

  const { data, isLoading, reload } = useAsync({
    promiseFn: API.getBroadcastSearch,
    keyword: decodeString(keyword.trim()),
    nextKey,
    isSearch: true,
    limit: 10,
    // activeTab,
    // watch: keyword,
  })

  const [keywords, setKeywords] = useRecoilState(KEYWORD_UPDATE)
  const [broadcast, setBroadcast] = useState<BroadcastItem[]>([])
  const [input, setInput] = useState<string>('')
  const pageSearchRef = useRef<HTMLDivElement>(null)
  const { customSearchBar } = getConfig()
  const root = useRecoilValue(STORE_ROOT_PROPS)
  const partnerId = getPartnerId()
  const isBridge = root?.bridge

  const history = useHistory()
  const location = useLocation()

  /**
   * @name EventHandler
   **/
  // const handleClickTab = (active: string) => {
  //   /***
  //    * 탭 클릭시 useState로 activeTab 값을 변경, useEffect로 activeTab값을 감지
  //    * 이전 activeTab값과 지금 선택된 activeTab값을 비교해서 값이 다를때만 broadcast객체를 초기화
  //    * */
  //   setActiveTab(active)
  //   setNextKey('init')
  // }

  const handleClickBack = useCallback(() => {
    new GAHelper().sendEvent(GA_EVENT.CLICK_BACK, {
      partnersId: partnerId,
      location: getLocation(location),
    })
    setNextKey('init')
    history.goBack()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history])

  const handleChangeInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value)
  }, [])

  const handleSearchKeyword = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (input) {
        const keyword = input
        const index = keywords.indexOf(keyword)
        if (index < 0) {
          setKeywords([keyword, ...keywords])
        } else {
          const _keywords = keywords.filter((x) => keyword !== x)
          setKeywords([keyword, ..._keywords])
        }

        new GAHelper().sendEvent(GA_EVENT.CLICK_SEARCH, {
          partnersId: partnerId,
          location: getLocation(location),
        })
        setNextKey('init')
        history.push(`/search/${encodeURIComponent(keyword)}${location.search}`)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [keywords, setKeywords, history, input, location.search],
  )
  const handleLoadMore = throttle(
    useCallback(() => {
      const { scrollY, innerHeight, document } = window
      if (document.body.scrollHeight - scrollY - 250 < innerHeight && !isLoading) {
        if (nextKey) {
          reload()
        }
      }
    }, [nextKey, reload, isLoading]),
    1000,
  )

  const handleClickSearchDelete = useCallback(() => {
    setInput('')

    new GAHelper().sendEvent(GA_EVENT.CLICK_CLEAR, {
      partnersId: partnerId,
      location: getLocation(location),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClickBroadcast = useCallback(
    ({
        link,
        broadcastId,
        broadcastTypeCode,
        broadcastName,
      }: {
        link?: string
        broadcastId?: string
        isBridge?: boolean
        broadcastTypeCode?: string
        broadcastName?: string
        order?: number
      }) =>
      () => {
        if (!link) return

        if (marketingGTM?.checkAvailability()) {
          const queryStringPrefix = link.indexOf('?') >= 0 ? '&' : '?'
          link = link.concat(`${queryStringPrefix}_gl=${marketingGTM.getGl()}`)
        }

        const params: BroadcastParams = {
          broadcastUrl: link,
        }
        if (ONLY_POST_MSG_PARTNERS.includes(partnerId)) {
          params.broadcastId = broadcastId
        }

        new GAHelper().sendEvent(GA_EVENT.CLICK_CONTENT, {
          partnersId: partnerId,
          location: getLocation(location),
          broadcastTypeCode: broadcastTypeCode,
          broadcastName: broadcastName,
          position: `-`,
        })

        if (getExecutorForPartner(partnerId)?.reqMoveBroadcastBridge?.(link)) {
          return
        }
        if (root?.bridge || window[BRIDGE.BROADCAST]) {
          requestBridge(BRIDGE.BROADCAST, params)
        } else {
          moveToLink({
            airbridge,
            link,
            type: isOpenLink ? LINK_MOVE_TYPE.REPLACE : LINK_MOVE_TYPE.REDIRECT,
          })
        }
      },
    [airbridge, isOpenLink, location, marketingGTM, partnerId, root?.bridge],
  )

  /**
   * @name DOMElement
   **/
  const broadcastEl = useMemo(() => {
    const token = getAccessToken()

    return broadcast.map((item, i) => {
      const {
        broadcastId,
        broadcastName: title,
        broadcastStateCode,
        broadcastThumbnailList,
        outputPath,
        partnerProfileImageUrl: shopLogo,
        previewUrl,
        room,
        shopName,
        totalDuration: playTime,
        usePurchaseEvent: isShowPurchaseAuthBadge,
      } = item
      const incomingCounterInfo = room?.incomingCounterInfo

      const representImage = broadcastThumbnailList?.filter((x) => x.isRepresentative === true)

      const queryString = parseObjectToQueryString({
        accessToken: token,
        npc: `search`,
        ...filterObject(query, (k) => Boolean(k.startsWith('utm'))),
      })

      const link = outputPath + queryString
      const isLive = broadcastStateCode === 'onair'

      return (
        <Card
          small
          key={broadcastId}
          broadcastId={broadcastId}
          broadcastStateCode={broadcastStateCode}
          link={link}
          isOpenLink={isOpenLink}
          header={{ isLive, playTime, incomingCounterInfo }}
          bottom={{ isShowPurchaseAuthBadge }}
          stream={previewUrl ?? undefined}
          thumbnail={representImage[0].thumbnailUrl}
          info={{ shopName, title, shopLogo }}
          isBridge={!!isBridge}
          root={root}
          order={i}
          handleClickBroadcast={handleClickBroadcast}
        />
      )
    })
  }, [broadcast, isOpenLink, isBridge, root, handleClickBroadcast, query])

  const info = useMemo(() => {
    if (data && data.totalCount) {
      return t('videos', { count: data.totalCount })
    }
  }, [data, t])

  /**
   * @name LifeCycleHook
   **/
  useEffect(() => {
    setInput(keyword)
    setBroadcast([])
    setNextKey('init')
    setTimeout(() => {
      reload()
    })

    return () => {
      setNextKey('init')
    }
  }, [keyword, reload])

  // useEffect(() => {
  //   const { innerHeight } = window
  //
  //   if (latestActiveTab.current !== activeTab) {
  //     latestActiveTab.current = activeTab
  //     setBroadcast([])
  //     reload()
  //     return
  //   } else if (pageSearchRef.current) {
  //     if (nextKey === 'init') return
  //     if (nextKey && innerHeight > pageSearchRef.current.clientHeight) {
  //       reload()
  //     }
  //   }
  // }, [nextKey, reload, activeTab])

  useEffect(() => {
    if (data && data.items) {
      setBroadcast((prev) => [...prev, ...data.items])
      const nextKey = data ? data.nextKey : ''
      setNextKey(nextKey)
    }
  }, [data])

  useEffect(() => {
    window.addEventListener('scroll', handleLoadMore)
    return () => {
      window.removeEventListener('scroll', handleLoadMore)
    }
  }, [handleLoadMore])

  const smallClassName = getPartnerId() === 'lg-uplus' ? 'small_sf' : 'small'
  return (
    <StylePageSearchList ref={pageSearchRef} smallClassName={smallClassName}>
      <PageHeader border onSubmit={handleSearchKeyword} id="sf_searchResult">
        <ButtonBack onClick={handleClickBack} />
        <SearchInput onChange={handleChangeInput} value={input} placeholder={t('search_item')} />
        {input.length > 0 && <ButtonDelete onClick={handleClickSearchDelete} />}
        <ButtonSearch customSearchBar={customSearchBar} />
      </PageHeader>

      {/*
      기능개발 보류로 중단
      <PageTab
        active={activeTab}
        onClick={handleClickTab}
        broadcastCount={data?.totalCount || 0}
        clipCount={data?.totalCount || 0}
      /> */}

      <PageContent info={info} className="curationList">
        {broadcastEl}
      </PageContent>

      {nextKey && <Loading isLoading />}
      <EmptyItem keyword={keyword} visible={!isLoading && broadcast.length === 0} />
    </StylePageSearchList>
  )
}

const StylePageSearchList = styled.div<{ smallClassName: 'small' | 'small_sf' }>`
  .curationList {
    > .${(props) => props.smallClassName} {
      padding-left: 16px;
      margin-bottom: 40px;
      display: flex;
      flex-direction: column;
      align-items: center;
      .card_wrapper {
        width: 100%;
        height: unset;
        padding-top: 134%;
      }
      .card_info {
        width: 100%;
      }
    }

    @media (max-width: 517px) {
      > div {
        width: 50%;
      }
    }
    @media (min-width: 518px) and (max-width: 768px) {
      > div {
        width: 33.3%;
      }
    }
    @media (min-width: 769px) and (max-width: 980px) {
      > div {
        width: 25%;
      }
    }
    @media (min-width: 981px) and (max-width: 1200px) {
      > div {
        width: 20%;
      }
    }
    @media (min-width: 1201px) {
      > div {
        width: 16.66%;
      }
    }
  }
`

export default PageSearchResult
